import type { PicklistMrsSummaryDto } from '../../types';
import { qty } from '../../utils/picklistProgress';

/**
 * PT-7 B — the ONE MRS hover card per page, as pure functions (vitest-covered; the repo has no jsdom).
 *
 * Why one card, never one per chip: a Radix root per chip is what turned one picklist-cart tick into a 239 ms
 * long task (the 239 ms lesson). So chips mount nothing and only tell a store which chip is open; one host per
 * page draws one card.
 */

/** The attribute on the card. PT-5's Post dialog ignores pointer-downs inside it (`isInsideMrsHoverCard`). */
export const MRS_HOVER_CARD_ATTR = 'data-mrs-hover-card';

/** The card's fixed id — there is only one per page — so a focused chip can point `aria-describedby` at it. */
export const MRS_HOVER_CARD_ID = 'mrs-hover-card';

/** The rect fields the position reads — a `DOMRect` has them all. */
export type RectLike = Pick<DOMRect, 'left' | 'top' | 'bottom' | 'width' | 'height'>;

export interface CardPosition {
  left: number;
  top: number;
  transform: 'none' | 'translateY(-100%)';
}

/** The card's width (300) plus the prototype's 16px clearance to the host's right edge (L1904-1907). */
const CARD_CLEARANCE = 316;

/**
 * Below the chip when more than 260px of the host remains under it, else above it. Left-aligned with the chip,
 * clamped 8px inside the host. A 6px gap either way. Relative to the host (`position:absolute` in the content
 * column, which is `relative` and does not itself scroll), so no scroll offset enters.
 */
/**
 * Marks an element holding several chips side by side or stacked (a list cell, the detail header). The card opens
 * against the GROUP's edge, not the hovered chip's, so it never lands on a sibling chip: owner 2026-10-07, after the
 * e2e walk showed a card for the top chip of a stacked cell covering the chip under it — the pointer, moving down,
 * entered the card and kept it open, so the second chip could not be reached.
 */
export const CHIP_GROUP_ATTR = 'data-mrs-chip-group';

/**
 * What the card is placed against: the chip's own left and width (the card stays aligned with the chip), and the
 * group's top and bottom when the chip sits in one. Without a group, the chip itself (today's placement).
 */
export function anchorRect(chip: RectLike, group: RectLike | null): RectLike {
  if (!group) return chip;
  return { left: chip.left, width: chip.width, top: group.top, bottom: group.bottom, height: group.bottom - group.top };
}

export function cardPosition(chip: RectLike, host: RectLike): CardPosition {
  const below = host.top + host.height - chip.bottom > 260;
  return {
    left: Math.max(8, Math.min(chip.left - host.left, host.width - CARD_CLEARANCE)),
    top: below ? chip.bottom - host.top + 6 : chip.top - host.top - 6,
    transform: below ? 'none' : 'translateY(-100%)',
  };
}

export interface CardStrings {
  /** Released as a percentage of requested: the green segment's width and the bold figure. */
  pct: number;
  /** Picked on picklists, not issued yet: the blue segment, never wider than what is left of the bar. */
  pickPct: number;
  mrsSum: string;
  issuedLeg: string;
  /** Shown only when something is picked and not yet issued. */
  pickLeg: string | null;
  mrsLines: string;
  mrsBarTip: string;
}

/**
 * The card's figures, verbatim from the prototype's retained code (`mrsPopData`, L1877-1899), reading PT-1's
 * summary. The blue segment counts EVERY open picklist (O-17; the prototype comment at L1877-1878 says "this
 * picklist's share" and contradicts its own code and tooltip). Mixed units read "units" and the tooltip says the
 * total is a display sum (owner ruling 2026-08-20).
 */
export function cardStrings(summary: PicklistMrsSummaryDto, mrsNumber: string): CardStrings {
  const requested = summary.requestedQuantity ?? 0;
  const released = summary.releasedQuantity ?? 0;
  const picked = summary.pickedNotIssuedQuantity ?? 0;
  const uWord = summary.unitOfMeasure ?? 'units';
  const den = Math.max(requested, 1);
  const pct = Math.round((released / den) * 100);
  const lines = summary.lineCount ?? 0;

  return {
    pct,
    pickPct: Math.min(100 - pct, Math.round((picked / den) * 100)),
    mrsSum: `${qty(released)} of ${qty(requested)} ${uWord}`,
    issuedLeg: `${qty(released)} ${uWord} issued`,
    pickLeg: picked > 0 ? `${qty(picked)} ${uWord} picked, not issued yet` : null,
    mrsLines: `${summary.linesFullyIssued ?? 0} of ${lines} ${lines === 1 ? 'line' : 'lines'} fully issued`,
    mrsBarTip:
      `${qty(released)} of ${qty(requested)} ${uWord} issued against ${mrsNumber} from any picklist`
      + (picked > 0 ? ` · ${qty(picked)} more picked on picklists, not issued yet` : '')
      + (summary.unitOfMeasure ? '.' : ' — lines use different units, so this total is a display sum.'),
  };
}

/**
 * Does this Escape close the card? Only the key decides — deliberately NOT `defaultPrevented`.
 *
 * PT-5's Post dialog keeps itself open on an Escape meant for the card by calling `preventDefault()` in its
 * Radix `onEscapeKeyDown` (Radix answers on `document` in the capture phase, before this bubble listener). The
 * common "return early when defaultPrevented" would then leave BOTH the dialog and the card open.
 */
export function closesOnEscape(e: { key: string; defaultPrevented: boolean }): boolean {
  return e.key === 'Escape';
}

/**
 * The card's "Requested before {Mon D}" date — the picklist's creation, the one moment every MRS on it is known
 * to have been raised by (the O-20 settlement: short month and day, e.g. "Jul 28"). `locale` is for tests; the
 * card passes none, so the viewer's own locale orders it. An unreadable date reads "—" rather than "Invalid Date".
 */
export function requestedBeforeLabel(iso: string, locale?: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

/** True when the target is an element inside the card. PT-5's dialog ignores outside-clicks that land here. */
export function isInsideMrsHoverCard(target: EventTarget | null): boolean {
  // `Element` does not exist outside a browser (vitest runs in node), so test it before `instanceof`.
  if (typeof Element === 'undefined' || !(target instanceof Element)) return false;
  return target.closest(`[${MRS_HOVER_CARD_ATTR}]`) !== null;
}
